import {
  CardBody,
  CardGroup,
  StepIcon,
  CardSubtitle,
  Badge,
  CardTitle,
  CardWrapper,
} from "@cdkglobal/radial";
import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";
import { useUserContext } from "../../../../../contexts/UserContext";
import { RowData, RowDataReducerAction } from "../../../../../types";
import styled from "styled-components";
import MapThumbnail from "./MapThumbnail";
import { ActiveSwitch } from "./ActiveSwitch";
import {
  FALLBACK,
  formatMMDDYYYY,
  str,
  strOrFallback,
} from "./utils/fieldUtils";

const ClickableCardWrapper = styled(CardWrapper)<{
  $isDragging: boolean;
  $isDisabled: boolean;
}>`
  display: flex;
  flex-direction: column;

  cursor: ${(props) => (props.$isDisabled ? "pointer" : "grab")};
  border: ${(props) =>
    props.$isDragging ? "2px solid #1a1a1a" : "1px solid #c5c5c5"};
  background-color: ${(props) => (props.$isDragging ? "#eef5fd" : "inherit")};
  box-shadow: ${(props) =>
    props.$isDragging ? "0 8px 24px rgba(0,0,0,.18)" : "none"};

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:active {
    cursor: ${(props) => (props.$isDisabled ? "pointer" : "grabbing")};
  }
`;

const TitleRow = styled.div`
  margin-bottom: 12px;
`;

const TitleInnerRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const TitleNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const StyledCardTitle = styled(CardTitle)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledBadge = styled(Badge)`
  flex-shrink: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 0 0 10px;
`;

const SubtitleWrapper = styled.div`
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
`;

const FooterMeta = styled.div`
  min-width: 0;
  flex: 1;
  min-height: 34px;

  color: #64748b;
  font-size: 12px;
  line-height: 1.25;

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const getArrayCount = (value: unknown): number =>
  Array.isArray(value) ? value.length : 0;

const getZoneCount = (building: RowData): number => {
  return getArrayCount(building.zones);
};

/**
 * Safely parse an updated_at value into a valid Date, or null if missing/invalid.
 * Guards against `formatMMDDYYYY(new Date(undefined))` rendering "NaN/NaN/NaN".
 */
const parseValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

interface BuildingCardProps {
  readonly building: RowData;
  readonly index: number;
  readonly onEdit: () => void;
  readonly dispatch: React.Dispatch<RowDataReducerAction>;
  readonly isDragging: boolean;
  readonly dndAttributes?: DraggableAttributes;
  readonly dndListeners?: DraggableSyntheticListeners;
}

export function BuildingCard({
  building,
  index,
  onEdit,
  dispatch,
  isDragging,
  dndAttributes,
  dndListeners,
}: BuildingCardProps) {
  const buildingName = strOrFallback(building.name);
  const street = str(building.address_line_1);
  const line2 = str(building.address_line_2);
  const cityStateLine = [str(building.city), str(building.state)]
    .filter(Boolean)
    .join(", ");
  const cityLine =
    cityStateLine + (building.zip ? ` ${str(building.zip)}` : "") || FALLBACK;

  const isActive = Boolean(building.is_enabled ?? false);
  const modifiedBy = strOrFallback(building.updated_by);
  const validUpdatedAt = parseValidDate(building.updated_at);
  const modifiedDate = validUpdatedAt
    ? formatMMDDYYYY(validUpdatedAt)
    : FALLBACK;
  const id = str(building.dms_building_id) || String(index);
  const zoneCount = getZoneCount(building);
  const { permissions } = useUserContext();
  const isDisabled = !permissions.setupPermission.hasManage;

  return (
    <ClickableCardWrapper
      onClick={onEdit}
      {...dndAttributes}
      {...dndListeners}
      $isDragging={isDragging}
      $isDisabled={isDisabled}
    >
      <MapThumbnail building={building} />

      <CardBody>
        <TitleRow>
          <CardGroup>
            <TitleInnerRow>
              <TitleNameRow>
                <StepIcon
                  dataTestId={`building-priority-${id}`}
                  stepNumber={index + 1}
                  isActive={index === 0}
                />
                <StyledCardTitle title={buildingName}>
                  {buildingName}
                </StyledCardTitle>
              </TitleNameRow>
              <StyledBadge badgeColor="#1262b3" id={"badge-zones"}>
                {zoneCount} Zones
              </StyledBadge>
            </TitleInnerRow>
            <SubtitleWrapper>
              <CardSubtitle>
                {street || FALLBACK}
                {line2 && `, ${line2}`}
                <br />
                {cityLine}
              </CardSubtitle>
            </SubtitleWrapper>
          </CardGroup>
        </TitleRow>

        <Divider />

        <FooterRow>
          <FooterMeta>
            {modifiedBy && (
              <span title={`Modified by ${modifiedBy}`}>
                Modified by {modifiedBy}
              </span>
            )}

            {modifiedDate && (
              <span title={modifiedDate}>on {modifiedDate}</span>
            )}
          </FooterMeta>
          <ActiveSwitch
            active={isActive}
            id={id}
            rowData={building}
            dispatch={dispatch}
          />
        </FooterRow>
      </CardBody>
    </ClickableCardWrapper>
  );
}
